import * as z from "zod";
import { isSerializedMap, reviveMap, reviveSet } from "./json";
import { Log } from "@greeter/log";

/**
 * This supports three formats for the json maps.
 * { "__type": "Map", "value": Array<[key, value]> }
 * { key: value }
 * and Array<[key, value]>
 */
export function JsonMapSchema<
  TKey extends z.ZodTypeAny,
  TValue extends z.ZodTypeAny
>(k: TKey, v: TValue) {
  return z.any().transform((o) => {
    if (!o) {
      Log.debug("[JsonMapSchema] input is undefined, returning default Map");
      return new Map<z.infer<typeof k>, z.infer<typeof v>>();
    }

    /**
     * Support for the { "__type": "Map", "value": Array<[key, value]> } format
     */
    if (isSerializedMap(o)) {
      let _m = reviveMap<z.infer<typeof k>, z.infer<typeof v>>(o);
      const n = new Map<z.infer<typeof k>, z.infer<typeof v>>();
      for (const [_k, _v] of _m.entries()) {
        n.set(k.parse(_k), v.parse(_v));
      }
      return n;
    }

    const m = new Map<z.infer<typeof k>, z.infer<typeof v>>();

    /**
     * Support for the Array<[key, value]> tuple
     */
    if (Array.isArray(o)) {
      for (let i = 0, l = o.length; i < l; i++) {
        const tuple = o[i];

        if (Array.isArray(tuple) && tuple.length === 2) {
          const _k = k.parse(tuple[0]);
          const _v = v.parse(tuple[1]);
          m.set(_k, _v);
        } else {
          throw new Error("Tuple is not of type Array or of length 2");
        }
      }
    } else if (typeof o === "object") {
      /**
       * Support for the object { key: value }
       */
      for (let [_k, _v] of Object.entries(o)) {
        // TODO: We should probably fix this?
        // @ts-ignore
        _k = k.parse(_k);
        _v = v.parse(_v);

        // @ts-ignore
        m.set(_k, _v);
      }
    } else {
      throw Error(
        `Unsupported serialized format: ${o}, expected { "__type": ..., value: Array<[key, value]> }, Array<[key, value]> or { key: value }`
      );
    }

    return m;
  });
}

export function JsonSetSchema<TValue extends z.ZodTypeAny>(v: TValue) {
  return z
    .object({
      __type: z.literal("Set"),
      value: z.array(v),
    })
    .transform((o) => reviveSet<z.infer<typeof v>>(o));
}

export function hasErrors<T extends z.ZodTypeAny>(
  errors?: z.ZodFlattenedError<T>
) {
  return Array.from(Object.entries(errors?.fieldErrors ?? {})).length > 0;
}
